import { useCallback, useEffect, useState } from 'react';
import {
  AnalysisEntitlement,
  fetchAnalysisEntitlement,
  NO_ENTITLEMENT,
} from '../../services/subscriptionService';

// Session-level cache: the entitlement is re-checked when the manager
// re-enters the Analysis section after a full page load, not on every
// tab switch within it.
let cachedEntitlement: AnalysisEntitlement | null = null;

export interface AnalysisEntitlementState {
  entitlement: AnalysisEntitlement;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useAnalysisEntitlement(): AnalysisEntitlementState {
  const [entitlement, setEntitlement] = useState<AnalysisEntitlement>(
    cachedEntitlement ?? NO_ENTITLEMENT
  );
  const [loading, setLoading] = useState(cachedEntitlement === null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (force: boolean) => {
    if (cachedEntitlement && !force) {
      setEntitlement(cachedEntitlement);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAnalysisEntitlement();
      cachedEntitlement = result;
      setEntitlement(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check subscription');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  const refresh = useCallback(() => {
    load(true);
  }, [load]);

  return { entitlement, loading, error, refresh };
}
